import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Plus, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { isTech } from "../lib/roles";
import { VIS_TYMOVY } from "../lib/taskVisibility";
import { TaskForm } from "../components/tasks/TaskForm";
import { TaskRow } from "../components/tasks/TaskRow";
import { TaskFilters } from "../components/tasks/TaskFilters";

const EMPTY = { title: "", description: "", assignee_id: "", job_id: "", due_date: "", visibility: VIS_TYMOVY };
const NO_FILTERS = { visibility: "", job_id: "", assignee_id: "", due: "" };

export default function Tasks() {
    const { user } = useAuth();
    const tech = isTech(user);
    const TABS = [
        { key: "moje", label: "Moje úkoly" },
        { key: "tymove", label: "Týmové" },
        ...(tech ? [] : [{ key: "firemni", label: "Firemní" }, { key: "vse", label: "Vše" }]),
        { key: "hotove", label: "Hotové" },
    ];
    const [tab, setTab] = useState("moje");
    const [filters, setFilters] = useState(NO_FILTERS);
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [sent, setSent] = useState({});
    const [editing, setEditing] = useState(null);
    const [team, setTeam] = useState([]);
    const [jobs, setJobs] = useState([]);
    const reqId = useRef(0);
    const todayISO = new Date().toISOString().slice(0, 10);

    const load = () => {
        const id = ++reqId.current;
        const p = new URLSearchParams({ tab, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
        api.get(`/tasks?${p}`).then((r) => { if (id === reqId.current) setItems(r.data); }).catch(() => toast.error("Načtení úkolů selhalo"));
        api.get("/tasks/counts").then((r) => setCounts(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, [tab, filters]); // eslint-disable-line

    useEffect(() => {
        api.get("/team/members").then((r) => setTeam((r.data || []).filter((m) => m.is_active !== false))).catch(() => {});
        api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    }, []);

    const save = async () => {
        if (!editing.title) { toast.error("Zadejte název úkolu"); return; }
        const payload = { title: editing.title, description: editing.description || "", visibility: editing.visibility || VIS_TYMOVY,
                          assignee_id: editing.assignee_id || null, job_id: editing.job_id || null, due_date: editing.due_date || null };
        try {
            if (editing.id) await api.put(`/tasks/${editing.id}`, payload);
            else await api.post("/tasks", payload);
            toast.success("Úkol uložen");
            setEditing(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };

    const toggle = async (t) => {
        try {
            const { data } = await api.post(`/tasks/${t.id}/toggle`);
            if (data.done) toast.success("Úkol dokončen");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Změna selhala"); }
    };

    const del = async (id) => {
        if (!window.confirm("Smazat úkol?")) return;
        try { await api.delete(`/tasks/${id}`); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Smazání selhalo"); }
    };

    const markSent = async (t) => {
        const s = sent[t.id] || {};
        try { await api.post(`/tasks/${t.id}/mark-sent`, { method: s.method || "email", date: s.date || todayISO }); toast.success("Odeslání zaznamenáno u faktury, úkol uzavřen"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };

    return (
        <div className="px-4 sm:px-10 py-6 sm:py-10 max-w-5xl mx-auto animate-fade-in" data-testid="tasks-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Kancelář</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Úkoly</h1>
                    <p className="text-sm text-slate-600 mt-2">Firemní vidí jen vedení, Týmové všichni, Soukromé jen autor a přiřazený.</p>
                </div>
                <button onClick={() => setEditing({ ...EMPTY })} data-testid="new-task-button"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Plus className="w-4 h-4" /> Nový úkol
                </button>
            </div>

            <div className="flex gap-1 border-b-2 border-slate-900 mb-4 overflow-x-auto" data-testid="task-tabs">
                {TABS.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)} data-testid={`task-tab-${t.key}`}
                            className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider whitespace-nowrap border-b-2 -mb-0.5 transition-colors ${tab === t.key ? "border-orange-600 text-slate-900 bg-orange-50" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
                        {t.label}{counts[t.key] != null && <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-sm ${tab === t.key ? "bg-orange-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`task-count-${t.key}`}>{counts[t.key]}</span>}
                    </button>
                ))}
            </div>

            <TaskFilters filters={filters} setFilters={setFilters} team={team} jobs={jobs} tech={tech} tab={tab} />

            <div className="bg-white border border-slate-200">
                {items.length === 0 ? (
                    <div className="p-16 text-center" data-testid="tasks-empty">
                        <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                        <div className="font-display text-lg font-bold text-slate-700">Žádné úkoly</div>
                        <p className="text-sm text-slate-500 mt-1">{tab === "hotove" ? "Zatím nic dokončeného." : "Vše hotovo — nebo zadejte první úkol."}</p>
                    </div>
                ) : items.map((t) => (
                    <TaskRow key={t.id} t={t} todayISO={todayISO} onToggle={toggle} onEdit={setEditing} onDelete={del}
                             sent={sent} setSent={setSent} onMarkSent={markSent} office={!tech} />
                ))}
            </div>

            {editing && <TaskForm editing={editing} setEditing={setEditing} onSave={save} onClose={() => setEditing(null)} team={team} jobs={jobs} tech={tech} />}
        </div>
    );
}
